import React from 'react';

import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import { LobMultiSelect } from '~/components/TPA/LOB/LobMultiselect';
import SubOrganizationMultiSelect from '~/components/TPA/SubOrganizations/SubOrganizationMultiSelect';
import { SlaRuleDTOSlaEntityLevel } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import { ENTITY_LEVEL_LABELS, TEXTS } from '../texts';
import type { SlaEntityLevelFilter, SysconfigOrganization } from '../types';

interface SlaRulesFiltersProps {
  organization: SysconfigOrganization;
  subOrgIds: number[];
  entityLevel: SlaEntityLevelFilter;
  onSubOrgChanged: (subOrgIds: number[]) => void;
  onLobsChanged: (lobs: string[]) => void;
  onEntityLevelChanged: (entityLevel: SlaEntityLevelFilter) => void;
}

const SlaRulesFilters: React.FC<SlaRulesFiltersProps> = ({
  organization,
  subOrgIds,
  entityLevel,
  onSubOrgChanged,
  onLobsChanged,
  onEntityLevelChanged,
}) => (
  // Column count follows the fields actually rendered: a flat organization has no sub-org select,
  // and a fixed three-track grid would leave it a dead trailing column.
  <div
    className={cn(
      'grid items-end gap-16 pb-32',
      organization.sub_organizations_enabled ? 'grid-cols-3' : 'grid-cols-2'
    )}
  >
    {organization.sub_organizations_enabled ? <SubOrganizationMultiSelect onChange={onSubOrgChanged} /> : null}
    <LobMultiSelect
      // `LobMultiSelect` is an untyped JS component whose `subOrganizationIds` PropTypes.array
      // infers as `never[]`, which `number[]` is not assignable to. Suppressed rather than cast:
      // a cast would keep compiling once that component gains real types, hiding the fact that the
      // workaround is obsolete, whereas this directive turns into an "unused" error (TS2578) the
      // day the underlying type is fixed.
      // @ts-expect-error -- untyped JS component: PropTypes.array infers as never[]
      subOrganizationIds={subOrgIds}
      onChange={onLobsChanged}
    />
    <SingleSelectField<SlaEntityLevelFilter>
      label={TEXTS.LEVEL_LABEL}
      fullWidth
      autoSelectSingleOption={false}
      value={entityLevel}
      onChange={(value) => onEntityLevelChanged(value)}
    >
      <MenuItem value="all">{TEXTS.ALL}</MenuItem>
      {Object.values(SlaRuleDTOSlaEntityLevel).map((level) => (
        <MenuItem key={level} value={level}>
          {ENTITY_LEVEL_LABELS[level]}
        </MenuItem>
      ))}
    </SingleSelectField>
  </div>
);

export default SlaRulesFilters;
